import {
  type ClientConfig,
  type IPosition,
  type Pixel,
} from "@sc07-canvas/lib/net";
import {
  type ClickEvent,
  type HoverEvent,
  PanZoom,
} from "@sc07-canvas/lib/renderer";
import EventEmitter from "eventemitter3";
import { toast } from "react-toastify";

import { CanvasUtils } from "./canvas.utils";
import { type CanvasPixel } from "./canvasRenderer";
import { KeybindManager } from "./keybinds";
import Network from "./network";
import { PaletteLib } from "./palette";
import { getRenderer } from "./utils";

const padSnapshotDatePart = (value: number) =>
  value.toString().padStart(2, "0");

interface CanvasAreaSnapshotMeta {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface CanvasCaptureArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface CanvasScreenRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

const formatSnapshotTimestamp = (date: Date) => {
  const year = date.getFullYear();
  const month = padSnapshotDatePart(date.getMonth() + 1);
  const day = padSnapshotDatePart(date.getDate());
  const hour = padSnapshotDatePart(date.getHours());
  const minute = padSnapshotDatePart(date.getMinutes());
  const second = padSnapshotDatePart(date.getSeconds());

  return `${year}-${month}${day}${hour}${minute}${second}`;
};

const formatSnapshotFilename = (date: Date, area?: CanvasAreaSnapshotMeta) => {
  const timestamp = formatSnapshotTimestamp(date);

  if (!area) return `canvas-${timestamp}.png`;

  return `canvas-${timestamp}-${area.left},${area.top}-${area.right},${area.bottom}.png`;
};

export interface CanvasSnapshot {
  id: number;
  filename: string;
  src: string;
  width: number;
  height: number;
  save: () => void;
}

interface CanvasEvents {
  /**
   * Cursor canvas position
   * (-1, -1) is not on canvas
   * @param position Canvas position
   * @returns
   */
  cursorPos: (position: IPosition) => void;
  surfacePointer: (position: {
    active: boolean;
    clientX: number;
    clientY: number;
    x?: number;
    y?: number;
  }) => void;
  canvasReady: () => void;
  snapshot: (snapshot: CanvasSnapshot) => void;
  pixelsChanged: () => void;
  pixelPlaced: (position: {
    clientX: number;
    clientY: number;
    color: number;
  }) => void;
}

export class CanvasCore extends EventEmitter<CanvasEvents> {
  private static instance: CanvasCore | undefined;

  private config: ClientConfig = {} as any;
  private canvas: HTMLCanvasElement | null = null;
  private PanZoom: PanZoom | null = null;

  private cursor: { x: number; y: number; color?: number } = { x: -1, y: -1 };
  private pixels: {
    [x_y: string]: { color: number; type: "full" | "pending" };
  } = {};
  lastPlace: number | undefined;

  private bypassCooldown = false;
  private placementInFlight = false;
  private destroyed = false;
  // private _delayedLoad: ReturnType<typeof setTimeout>;

  static get(): CanvasCore;
  static get<T extends boolean>(
    instanceIfNeeded: T,
  ): T extends true ? CanvasCore : CanvasCore | null;
  static get(instanceIfNeeded = true) {
    if (this.instance) return this.instance;

    if (!instanceIfNeeded) return null;

    return (this.instance = new this());
  }

  private constructor() {
    super();
    getRenderer().on("ready", () => this.emit("canvasReady"));

    Network.on("pixel", this.handlePixel);
    Network.on("square", this.handleSquare);
    Network.on("pixelLastPlaced", this.handlePixelLastPlaced);
    Network.waitForState("pixelLastPlaced").then(([time]) =>
      this.handlePixelLastPlaced(time),
    );
  }

  destroy() {
    this.destroyed = true;
    this.setPanZoom(null);
    this.setCanvas(null);
    getRenderer().off("ready");

    Network.off("pixel", this.handlePixel);
    Network.off("square", this.handleSquare);
    Network.off("pixelLastPlaced", this.handlePixelLastPlaced);

    if (CanvasCore.instance === this) {
      CanvasCore.instance = undefined;
    }
  }

  setCanvas(canvas: HTMLCanvasElement | null) {
    console.log("setCanvas called in canvascore", canvas);
    this.canvas?.removeEventListener("contextmenu", this.handleContextMenu);
    this.canvas = canvas;
    this.canvas?.addEventListener("contextmenu", this.handleContextMenu);
    this.loadRenderer();

    if (canvas) {
      getRenderer().startRender();
    } else {
      getRenderer().stopRender();
    }
  }

  isSurfaceReady() {
    return (
      !this.destroyed &&
      Boolean(this.canvas && this.PanZoom && this.PanZoom.$wrapper)
    );
  }

  private emitSnapshot(snapshot: Omit<CanvasSnapshot, "save">) {
    let saved = false;

    const save = () => {
      if (saved) return;
      saved = true;

      const $el = document.createElement("a");
      $el.setAttribute("download", snapshot.filename);
      $el.setAttribute("href", snapshot.src);
      document.body.appendChild($el);
      $el.click();
      $el.remove();
      window.setTimeout(() => URL.revokeObjectURL(snapshot.src), 60_000);
    };

    const handled = this.emit("snapshot", {
      ...snapshot,
      save,
    });

    if (!handled) {
      window.requestAnimationFrame(save);
    }
  }

  private createSnapshot(
    snapshot: Omit<CanvasSnapshot, "save" | "src">,
    targetName: string,
    area?: CanvasCaptureArea,
  ) {
    void getRenderer()
      .capture(area)
      .then((blob) => {
        this.emitSnapshot({
          ...snapshot,
          src: URL.createObjectURL(blob),
        });
      })
      .catch((error) => {
        console.error(`Unable to capture ${targetName}`, error);
        toast.info(`Unable to capture ${targetName}: can't create image`);
      });
  }

  captureCanvas() {
    if (!this.canvas) {
      toast.info("Unable to capture canvas: can't find canvas");
      return;
    }

    const id = Date.now();
    const [width, height] = this.config.canvas?.size ?? [
      this.canvas.width,
      this.canvas.height,
    ];

    this.createSnapshot(
      {
        id,
        filename: formatSnapshotFilename(new Date(id)),
        width,
        height,
      },
      "canvas",
    );
  }

  private captureArea({
    x,
    y,
    width,
    height,
    targetName,
  }: CanvasCaptureArea & {
    targetName: string;
  }) {
    if (!this.canvas) {
      toast.info(`Unable to capture ${targetName}: can't find canvas`);
      return;
    }

    const requestedLeft = Math.floor(x);
    const requestedTop = Math.floor(y);
    const requestedRight = Math.ceil(x + width);
    const requestedBottom = Math.ceil(y + height);
    if (requestedRight <= requestedLeft || requestedBottom <= requestedTop) {
      toast.info(`Unable to capture ${targetName}: area is empty`);
      return;
    }

    const sourceLeft = Math.max(0, requestedLeft);
    const sourceTop = Math.max(0, requestedTop);
    const [canvasWidth, canvasHeight] = this.config.canvas?.size ?? [
      this.canvas.width,
      this.canvas.height,
    ];
    const sourceRight = Math.min(canvasWidth, requestedRight);
    const sourceBottom = Math.min(canvasHeight, requestedBottom);
    const sourceWidth = sourceRight - sourceLeft;
    const sourceHeight = sourceBottom - sourceTop;

    if (sourceWidth <= 0 || sourceHeight <= 0) {
      toast.info(`Unable to capture ${targetName}: area is outside the canvas`);
      return;
    }

    const id = Date.now();
    const area = {
      left: sourceLeft,
      top: sourceTop,
      right: sourceLeft + sourceWidth - 1,
      bottom: sourceTop + sourceHeight - 1,
    };

    this.createSnapshot(
      {
        id,
        filename: formatSnapshotFilename(new Date(id), area),
        width: sourceWidth,
        height: sourceHeight,
      },
      targetName,
      {
        x: sourceLeft,
        y: sourceTop,
        width: sourceWidth,
        height: sourceHeight,
      },
    );
  }

  canvasAreaToScreenRect({
    x,
    y,
    width,
    height,
  }: CanvasCaptureArea): CanvasScreenRect {
    if (!this.canvas) {
      throw new Error("CanvasCore#canvasAreaToScreenRect : no <canvas>");
    }

    if (!this.PanZoom) {
      throw new Error(
        "CanvasCore#canvasAreaToScreenRect : no PanZoom instance",
      );
    }

    const rect = this.canvas.getBoundingClientRect();

    if (this.PanZoom.flags.useZoom) {
      const scale = this.PanZoom.transform.scale;

      return {
        left: (x + rect.left) * scale,
        top: (y + rect.top) * scale,
        width: width * scale,
        height: height * scale,
      };
    }

    const scaleX = rect.width / this.canvas.width;
    const scaleY = rect.height / this.canvas.height;

    return {
      left: rect.left + x * scaleX,
      top: rect.top + y * scaleY,
      width: width * scaleX,
      height: height * scaleY,
    };
  }

  captureView() {
    if (!this.canvas) {
      toast.info("Unable to capture view: can't find canvas");
      return;
    }

    if (!this.PanZoom) {
      toast.info("Unable to capture view: can't find viewport");
      return;
    }

    const rect = this.canvas.getBoundingClientRect();
    const screenToCanvas = (x: number, y: number) => {
      if (!this.canvas || !this.PanZoom) return { x: 0, y: 0 };

      if (this.PanZoom.flags.useZoom) {
        const scale = this.PanZoom.transform.scale;

        return {
          x: x / scale - rect.left,
          y: y / scale - rect.top,
        };
      }

      const scaleX = this.canvas.width / rect.width;
      const scaleY = this.canvas.height / rect.height;

      return {
        x: (x - rect.left) * scaleX,
        y: (y - rect.top) * scaleY,
      };
    };

    const topLeft = screenToCanvas(0, 0);
    const bottomRight = screenToCanvas(window.innerWidth, window.innerHeight);
    const left = Math.max(0, Math.floor(Math.min(topLeft.x, bottomRight.x)));
    const top = Math.max(0, Math.floor(Math.min(topLeft.y, bottomRight.y)));
    const right = Math.min(
      this.canvas.width,
      Math.ceil(Math.max(topLeft.x, bottomRight.x)),
    );
    const bottom = Math.min(
      this.canvas.height,
      Math.ceil(Math.max(topLeft.y, bottomRight.y)),
    );

    this.captureArea({
      x: left,
      y: top,
      width: right - left,
      height: bottom - top,
      targetName: "view",
    });
  }

  captureRegion(area: CanvasCaptureArea) {
    this.captureArea({
      ...area,
      targetName: "region",
    });
  }

  captureTemplateArea(area: CanvasCaptureArea) {
    this.captureArea({
      ...area,
      targetName: "template area",
    });
  }

  setPanZoom(panZoom: PanZoom | null) {
    this.PanZoom?.removeListener("hover", this.handleMouseMove);
    this.PanZoom?.removeListener("click", this.handleMouseDown);
    this.PanZoom?.removeListener("longPress", this.handleLongPress);

    this.PanZoom = panZoom;

    this.PanZoom?.addListener("hover", this.handleMouseMove);
    this.PanZoom?.addListener("click", this.handleMouseDown);
    this.PanZoom?.addListener("longPress", this.handleLongPress);
  }

  /**
   * React.Strict remounts the main component, causing a quick remount, which then causes errors related to webworkers
   *
   * If #useCanvas fails, it's most likely due to that
   */
  loadRenderer() {
    if (!this.canvas) return;

    try {
      getRenderer().useCanvas(this.canvas, "main");
    } catch (e) {
      console.warn(
        "[Canvas#loadRenderer] Failed at #useCanvas, this shouldn't be fatal",
        e,
      );
    }
  }

  setSize(width: number, height: number) {
    getRenderer().setSize(width, height);
  }

  loadConfig(config: ClientConfig) {
    this.config = config;

    this.setSize(config.canvas.size[0], config.canvas.size[1]);

    // we want the new one if possible
    // (this might cause a timing issue though)
    // if we don't clear the old one, if the canvas gets resized we get weird stretching
    if (Object.keys(this.pixels).length > 0)
      Network.clearPreviousState("canvas");

    // Network.waitForState("canvas").then(([pixels]) => {
    //   console.log("loadConfig just received new canvas data");
    //   this.handleBatch(pixels);
    // });

    Network.on("canvas", (start, end, pixels) => {
      console.log("[Canvas] received canvas section");
      this.handleBatch(start, end, pixels);
    });

    const chunks = Network.getCanvasChunks();
    console.log(`[Canvas] Received ${chunks.length} chunks to load`);
    let loaded = 0;
    for (const chunk of chunks) {
      console.log(`[Canvas] Loading canvas chunk ${loaded}...`);
      this.handleBatch(chunk.start, chunk.end, chunk.pixels);

      loaded++;
    }
  }

  hasConfig() {
    return !!this.config;
  }

  getConfig() {
    return this.config;
  }

  getPanZoom() {
    return this.PanZoom;
  }

  setCooldownBypass(value: boolean) {
    this.bypassCooldown = value;
  }

  getCooldownBypass() {
    return this.bypassCooldown;
  }

  getAllPixels() {
    const pixels: {
      x: number;
      y: number;
      color: number;
    }[] = [];

    for (const [x_y, value] of Object.entries(this.pixels)) {
      if (value.type === "pending") continue;

      const [x, y] = x_y.split("_").map((v) => parseInt(v));
      pixels.push({
        x,
        y,
        color: value.color,
      });
    }

    return pixels;
  }

  /**
   * Get nearby pixels
   * @param x
   * @param y
   * @param around (x,y) +- around
   */
  getSurroundingPixels(x: number, y: number, around: number = 3) {
    const pixels = [];

    for (let offsetY = 0; offsetY <= around + 1; offsetY++) {
      const arr = [];
      for (let offsetX = 0; offsetX <= around + 1; offsetX++) {
        const targetX = x + (offsetX - around + 1);
        const targetY = y + (offsetY - around + 1);
        const pixel = this.pixels[targetX + "_" + targetY];

        if (pixel) {
          arr.push("#" + (PaletteLib.getColor(pixel.color)?.hex || "ffffff"));
        } else {
          arr.push("transparent");
        }
      }
      pixels.push(arr);
    }

    return pixels;
  }

  getPixel(x: number, y: number): { color: number } | undefined {
    return this.pixels[x + "_" + y];
  }

  handleLongPress = (clientX: number, clientY: number) => {
    if (
      document.body.classList.contains("is-picking-color") ||
      document.body.classList.contains("is-capturing-region")
    ) {
      return;
    }

    KeybindManager.handleInteraction(
      {
        key: "LONG_PRESS",
        alt: false,
        ctrl: false,
        meta: false,
        shift: false,
      },
      {
        clientX,
        clientY,
      },
    );
  };

  previousCanvasClicks: { x: number; y: number }[] = [];

  private handleContextMenu = (event: MouseEvent) => {
    event.preventDefault();
  };

  handleMouseDown = (e: ClickEvent) => {
    if (this.destroyed) return;
    if (
      document.body.classList.contains("is-picking-color") ||
      document.body.classList.contains("is-capturing-region")
    ) {
      return;
    }

    const noMods = !e.alt && !e.ctrl && !e.meta && !e.shift;

    if (e.button === "MCLICK") {
      KeybindManager.handleInteraction(
        {
          key: "MCLICK",
          alt: e.alt,
          ctrl: e.ctrl,
          meta: e.meta,
          shift: e.shift,
        },
        {
          clientX: e.clientX,
          clientY: e.clientY,
        },
      );
      return;
    }

    if (noMods && (e.button === "LCLICK" || e.button === "RCLICK")) {
      const [x, y] = this.screenToPos(e.clientX, e.clientY);

      const color =
        e.button === "LCLICK"
          ? PaletteLib.getSelectedColor()?.id
          : PaletteLib.getSecondaryColor()?.id;
      const placement = this.place(x, y, color);

      void placement.then((placed) => {
        if (placed && color !== undefined) {
          this.emit("pixelPlaced", {
            clientX: e.clientX,
            clientY: e.clientY,
            color,
          });
        }
      });

      if (e.button === "RCLICK") {
        // keep track of the last X pixels right clicked
        // used by the ModModal to determine areas selected
        this.previousCanvasClicks.push({ x, y });
        this.previousCanvasClicks = this.previousCanvasClicks.slice(-2);
      }
    }
  };

  handleMouseMove = (e: HoverEvent) => {
    if (!this.canvas) return;

    const canvasRect = this.canvas.getBoundingClientRect();
    if (
      canvasRect.left <= e.clientX &&
      canvasRect.right >= e.clientX &&
      canvasRect.top <= e.clientY &&
      canvasRect.bottom >= e.clientY
    ) {
      const [x, y] = this.screenToPos(e.clientX, e.clientY);
      this.cursor.x = x;
      this.cursor.y = y;
    } else {
      this.cursor.x = -1;
      this.cursor.y = -1;
    }

    this.emit("cursorPos", this.cursor);

    const overSurface =
      e.target instanceof Node &&
      Boolean(this.PanZoom?.$wrapper.contains(e.target));
    if (overSurface) {
      const [x, y] = this.screenToPos(e.clientX, e.clientY);
      this.emit("surfacePointer", {
        active: true,
        clientX: e.clientX,
        clientY: e.clientY,
        x,
        y,
      });
    } else {
      this.emit("surfacePointer", {
        active: false,
        clientX: e.clientX,
        clientY: e.clientY,
      });
    }
  };

  handleSquare = (
    start: [x: number, y: number],
    end: [x: number, y: number],
    color: number,
  ) => {
    const palette = PaletteLib.getColor(color);
    const serializeBuild: CanvasPixel[] = [];

    for (let x = start[0]; x <= end[0]; x++) {
      for (let y = start[1]; y <= end[1]; y++) {
        // we still store a copy of the pixels in this instance for non-rendering functions
        this.pixels[x + "_" + y] = {
          type: "full",
          color: palette?.id ?? -1,
        };

        serializeBuild.push({
          x,
          y,
          hex:
            !palette || palette?.hex === "transparent" ? "null" : palette.hex,
        });
      }
    }

    getRenderer().usePixels(serializeBuild);
    this.emit("pixelsChanged");
  };

  handleBatch = (
    start: [x: number, y: number],
    end: [x: number, y: number],
    pixels: string[],
  ) => {
    if (!this.config.canvas) {
      throw new Error("handleBatch called with no config");
    }

    const serializeBuild: CanvasPixel[] = [];
    const width = end[0] - start[0];
    const height = end[1] - start[1];

    for (let x = 0; x < width; x++) {
      for (let y = 0; y < height; y++) {
        const hex = pixels[width * y + x];
        const palette = PaletteLib.getColorFromHex(hex);

        const canvasX = x + start[0];
        const canvasY = y + start[1];

        // we still store a copy of the pixels in this instance for non-rendering functions
        this.pixels[canvasX + "_" + canvasY] = {
          type: "full",
          color: palette?.id ?? -1,
        };

        serializeBuild.push({
          x: canvasX,
          y: canvasY,
          hex: hex === "transparent" ? "null" : hex,
        });
      }
    }

    getRenderer().usePixels(serializeBuild, true);
    this.emit("pixelsChanged");
  };

  handlePixel = ({ x, y, color }: Pixel) => {
    // we still store a copy of the pixels in this instance for non-rendering functions
    this.pixels[x + "_" + y] = {
      type: "full",
      color,
    };

    const palette = PaletteLib.getColor(color);

    getRenderer().usePixel({ x, y, hex: palette?.hex || "null" });
    this.emit("pixelsChanged");
  };

  handlePixelLastPlaced = (time: number) => {
    this.lastPlace = time;
  };

  /**
   * Changes the cursor color as tracked by the Canvas instance
   *
   * @see Toolbar/Palette.tsx
   * @param color
   */
  updateCursor(color?: number) {
    this.cursor.color = color;
  }

  place(x: number, y: number, color = PaletteLib.getSelectedColor()?.id) {
    if (color === undefined || this.placementInFlight) {
      return Promise.resolve(false);
    }

    this.placementInFlight = true;

    return Network.socket
      .emitWithAck(
        "place",
        {
          x,
          y,
          color,
        },
        this.bypassCooldown,
      )
      .then((ack) => {
        if (ack.success) {
          this.handlePixel(ack.data);
          Network.emit("placeSuccessful");
          return true;
        } else {
          if (ack.error === "pixel_already_pending") return false;

          Network.emit("placeFailed");
          console.warn(
            "Attempted to place pixel",
            { x, y, color },
            "and got error",
            ack,
          );

          switch (ack.error) {
            case "invalid_pixel":
              toast.error(
                "Cannot place, invalid pixel location. Are you even on the canvas?",
              );
              break;
            case "no_user":
              toast.error("You are not logged in.");
              break;
            case "palette_color_invalid":
              toast.error("This isn't a color that you can use...?");
              break;
            case "pixel_cooldown":
              toast.error("You're on pixel cooldown, cannot place");
              break;
            case "you_already_placed_that":
              toast.error("You already placed this color at this location");
              break;
            default:
              toast.error("Error while placing pixel: " + ack.error);
          }

          return false;
        }
      })
      .finally(() => {
        this.placementInFlight = false;
      });
  }

  canvasToPanZoomTransform(x: number, y: number) {
    if (!this.canvas) {
      throw new Error("CanvasCore#canvasToPanZoomTransform : no <canvas>");
    }

    const { transformX, transformY } = CanvasUtils.canvasToPanZoomTransform(
      x,
      y,
      [this.canvas.width, this.canvas.height],
    );

    return { transformX, transformY };
  }

  panZoomTransformToCanvas() {
    if (!this.canvas) {
      throw new Error("CanvasCore#panZoomTransformToCanvas : no <canvas>");
    }

    if (!this.PanZoom) {
      throw new Error(
        "CanvasCore#panZoomTransformToCanvas : no PanZoom instance",
      );
    }

    const { x, y, scale: zoom } = this.PanZoom.transform;
    const rect = this.canvas.getBoundingClientRect();

    let canvasX;
    let canvasY;

    if (this.PanZoom.flags.useZoom) {
      // css zoom doesn't change the bounding client rect
      // therefore dividing by zoom doesn't return the correct output
      canvasX = this.canvas.width - (x + rect.width / 2);
      canvasY = this.canvas.height - (y + rect.height / 2);
    } else {
      canvasX = this.canvas.width / 2 - (x + rect.width / zoom);
      canvasY = this.canvas.height / 2 - (y + rect.height / zoom);

      canvasX += this.canvas.width;
      canvasY += this.canvas.height;
    }

    canvasX >>= 0;
    canvasY >>= 0;

    return { canvasX, canvasY, zoom };
  }

  debug(x: number, y: number, id?: string) {
    if (document.getElementById("debug-" + id)) {
      document.getElementById("debug-" + id)!.style.top = y + "px";
      document.getElementById("debug-" + id)!.style.left = x + "px";
      return;
    }
    const el = document.createElement("div");
    if (id) el.id = "debug-" + id;
    el.classList.add("debug-point");
    el.style.setProperty("top", y + "px");
    el.style.setProperty("left", x + "px");
    document.body.appendChild(el);
  }

  /**
   * Canvas world position to screen coordinates.
   */
  worldToScreen(x: number, y: number) {
    if (!this.canvas) {
      throw new Error("CanvasCore#worldToScreen : no <canvas>");
    }

    const rect = this.canvas.getBoundingClientRect();
    return [
      rect.left + x * (rect.width / this.canvas.width),
      rect.top + y * (rect.height / this.canvas.height),
    ];
  }
  /**
   * Screen (clientX, clientY) to Canvas position
   * @param x
   * @param y
   * @returns
   */
  screenToPos(x: number, y: number) {
    if (!this.canvas) {
      throw new Error("CanvasCore#screenToPos : no <canvas>");
    }

    if (!this.PanZoom) {
      throw new Error("CanvasCore#screenToPos : no PanZoom instance");
    }

    // the rendered dimentions in the browser
    const rect = this.canvas.getBoundingClientRect();

    const output = {
      x: 0,
      y: 0,
    };

    if (this.PanZoom.flags.useZoom) {
      const scale = this.PanZoom.transform.scale;

      output.x = x / scale - rect.left;
      output.y = y / scale - rect.top;
    } else {
      // get the ratio
      const scale = [
        this.canvas.width / rect.width,
        this.canvas.height / rect.height,
      ];

      output.x = (x - rect.left) * scale[0];
      output.y = (y - rect.top) * scale[1];
    }

    // floor it, we're getting canvas coords, which can't have decimals
    output.x >>= 0;
    output.y >>= 0;

    return [output.x, output.y];
  }
}
